import React, { FunctionComponent, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { IDataPluginAuthorization } from 'src/models/components/IDataPluginAuthorization'
import { generateCodeChallenge, generateRandomString } from 'src/utils/pkceUtils';
import { fetchOAuthParams, storeCodeVerifier, fetchCodeVerifier, OAuthParams } from 'src/utils/oauth2Utils';
import { APIContext } from 'src/contexts/api';
import { logError } from 'src/utils/Error';
import { useGoogleLogin } from '@react-oauth/google'
import { Button } from '@material-ui/core';
import AppEnv from 'src/utils/appenv'

/*
 * Manages 'authorization_code' or 'implicit' (a.k.a. 'token' flow) grant flows.
 */
const DataPluginAuthorization: FunctionComponent<IDataPluginAuthorization> = (props: PropsWithChildren<IDataPluginAuthorization>) => {
    const LEGACY_GOOGLE_AUTHORIZATION_URL = 'https://oauth2.googleapis.com/token'
    const GOOGLE_AUTHORIZATION_URL = 'https://accounts.google.com/o/oauth2/v2/auth'
    const MICROSOFT_AUTHORIZATION_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize'
    const FACEBOOK_AUTHORIZATION_URL = 'https://www.facebook.com/v23.0/dialog/oauth'
    const LINKEDIN_AUTHORIZATION_URL = 'https://www.linkedin.com/oauth/v2/authorization'
    const QUICKBOOKS_AUTHORIZATION_URL = 'https://appcenter.intuit.com/app/connect/oauth2'
    const XERO_AUTHORIZATION_URL = 'https://login.xero.com/identity/connect/authorize'

    const {
      prefix,
      properties,
      errors,
      handleSettingsUpdate,
      consentRequiredCallback
    } = props;
    const { catalogAPI } = useContext(APIContext);

    // flag indicating authorization required and consent given
    const [authorizationState, setAuthorizationState] = useState({
        isAuthorizationRequired: false,
        isAuthorized: false,
    });

    // the URL of the identity providers authorization endpoint
    const authorizationUrl = properties[prefix ? `${prefix}.oauth_credentials.authorization_url` : 'oauth_credentials.authorization_url']
    const identifyProvider = authorizationUrl === LEGACY_GOOGLE_AUTHORIZATION_URL ? "oauth2-google"
      : authorizationUrl === GOOGLE_AUTHORIZATION_URL ? "oauth2-google"
      : authorizationUrl === MICROSOFT_AUTHORIZATION_URL ? "oauth2-microsoft"
      : authorizationUrl === FACEBOOK_AUTHORIZATION_URL ? "oauth2-facebook"
      : authorizationUrl === LINKEDIN_AUTHORIZATION_URL ? "oauth2-linkedin"
      : authorizationUrl === QUICKBOOKS_AUTHORIZATION_URL ? "oauth2-quickbooks"
      : authorizationUrl === XERO_AUTHORIZATION_URL ? "oauth2-xero"
      : null
    // while not used to redirect our application, validated on authorization and exchange steps
    const redirectUri = `${window.location.origin}/oauth`
    // the scope of access we are requesting
    const scope = properties[prefix ? `${prefix}.oauth_credentials.scope` : 'oauth_credentials.scope']
    // 'code' - from 'authorization_code' identity provider response
    // and any custom identity provider responses e.g. realm id on quickbooks
    const [oauthParams, setOAuthParams] = useState<OAuthParams | null>(null)
    // 'access_token' - from 'implicit' identity provider response
    const [accessToken, setAccessToken] = useState(null)
    // the popup monitored for OAuth exchange code
    const [externalPopup, setExternalPopup] = useState<any>(null);

    const connectToGoogle = useGoogleLogin({
      flow: 'auth-code',
      onSuccess: r => setOAuthParams({code: r.code}),
      onError: logError,
      scope: properties[prefix ? `${prefix}.oauth_credentials.scope` : 'oauth_credentials.scope']
    })

    const connect = async () => {
      // ensure the local storage is reset before popup
      fetchOAuthParams()
      const authUrl = async () => {
        if (authorizationUrl === GOOGLE_AUTHORIZATION_URL) {
          // Google parameters and redirection for OAuth2 authentication endpoint
          const params = new URLSearchParams({
            response_type: 'code',
            client_id: AppEnv.APP_OAUTH_GOOGLE_CLIENT_ID,
            redirect_uri: redirectUri,
            scope: scope,
            access_type: 'offline',
          })
          return `${authorizationUrl}?${params.toString()}`
        } else if (authorizationUrl === MICROSOFT_AUTHORIZATION_URL) {
          // Create and store a random string in storage, used as the PKCE 'code_verifier'
          const codeVerifier = generateRandomString(96)
          storeCodeVerifier(codeVerifier)
          // Hash and Base64URL encode the code_verifier, used as the 'code_challenge'
          return await generateCodeChallenge(codeVerifier).then((codeChallenge) => {
            // Microsoft parameters and redirection for OAuth2 authentication endpoint
            const params = new URLSearchParams({
              response_type: 'code',
              response_mode: 'query',
              client_id: AppEnv.APP_OAUTH_MICROSOFT_CLIENT_ID,
              redirect_uri: redirectUri,
              scope: scope,
              code_challenge: codeChallenge,
              code_challenge_method: 'S256'
            })
            return `${authorizationUrl}?${params.toString()}`
          })
        } else if (authorizationUrl === FACEBOOK_AUTHORIZATION_URL) {
            // Facebook parameters and redirection for OAuth2 authentication endpoint
            const params = new URLSearchParams({
              response_type: 'code',
              client_id: AppEnv.APP_OAUTH_FACEBOOK_CLIENT_ID,
              redirect_uri: redirectUri,
              config_id: scope // Facebook for Business login
            })
            return `${authorizationUrl}?${params.toString()}`
        } else if (authorizationUrl === LINKEDIN_AUTHORIZATION_URL) {
            // LinkedIn parameters and redirection for OAuth2 authentication endpoint
            const params = new URLSearchParams({
              response_type: 'code',
              client_id: AppEnv.APP_OAUTH_LINKEDIN_CLIENT_ID,
              redirect_uri: redirectUri,
              scope: scope,
            })
            return `${authorizationUrl}?${params.toString()}`
        } else if (authorizationUrl === QUICKBOOKS_AUTHORIZATION_URL) {
            // Quickbooks parameters and redirection for OAuth2 authentication endpoint
            const params = new URLSearchParams({
              response_type: 'code',
              client_id: AppEnv.APP_OAUTH_QUICKBOOKS_CLIENT_ID,
              redirect_uri: redirectUri,
              scope: scope,
              state: 'none',
            })
            return `${authorizationUrl}?${params.toString()}`
        } else if (authorizationUrl === XERO_AUTHORIZATION_URL) {
            // Xero parameters and redirection for OAuth2 authentication endpoint
            const params = new URLSearchParams({
              response_type: 'code',
              client_id: AppEnv.APP_OAUTH_XERO_CLIENT_ID,
              redirect_uri: redirectUri,
              scope: scope,
              state: 'none',
            })
            return `${authorizationUrl}?${params.toString()}`
        }
        return "noidp_error"
      }
      // all other OAuth consent managed with our externalPopup useEffect
      const ww = 700
      const hh = 800
      const left = window.screenX + (window.outerWidth - ww) / 2;
      const top = window.screenY + (window.outerHeight - hh) / 2.5;
      const title = `Consent`;
      const popup: null | WindowProxy = window.open(await authUrl(), title, `width=${ww},height=${hh},left=${left},top=${top}`);
      if (!popup) {
        console.warn('Popup blocked. Disable popup blocker to authorise data source.')
      }
      setExternalPopup(popup)
    }

    const disconnect = () => {
      setAuthorizationState({
        ...authorizationState,
        isAuthorized: false
      })

      // remove any associated properties
      const filtered = Object.entries(properties)
        .filter(([k]) => !!prefix && !k.startsWith(prefix))
        .reduce((obj, [k, v]) => ({...obj, [k]: v}), {})

      handleSettingsUpdate(filtered)
    }

    const googleConnectDisconnect = () => {
      if (!authorizationState.isAuthorized) {
        connectToGoogle()
        return
      }
      disconnect()
    }

    const connectDisconnect = async () => {
      if (!authorizationState.isAuthorized) {
        connect()
        return
      }
      disconnect()
    }

    /*
     * Exchange the authorization_code, received following user consent, for a
     * access_token / refresh_token to be used to call the request API.
     */
    const exchangeCode = async (authorizationUrl:string, code:string) => {
      const codeVerifier = fetchCodeVerifier()
      const exchangeBody = {
        grant_type: 'authorization_code',
        redirect_uri: authorizationUrl === LEGACY_GOOGLE_AUTHORIZATION_URL ? null : redirectUri,
        scope: scope,
        code: code,
        code_verifier: codeVerifier,
      }
      await catalogAPI
          .post(encodeURI(`/tokens/${identifyProvider}/token`), exchangeBody, { errorHandler: false })
          .then((resp: any) => {
            setOAuthParams(null)
            const data = resp.data;
            properties[prefix ? `${prefix}.oauth_credentials.access_token` : 'oauth_credentials.access_token'] = data.access_token
            properties[prefix ? `${prefix}.oauth_credentials.refresh_token` : 'oauth_credentials.refresh_token'] = data.refresh_token
            handleSettingsUpdate(properties)
          })
          .catch((e: any) => {
            logError(e);
          })
    }

    // Waits for the exchange code in the url of the popup
    useEffect(() => {
      if (!externalPopup) {
        return
      }

      const timer = setInterval(() => {
        if (!externalPopup) {
          timer && clearInterval(timer)
          return
        }
        try {
          const oauthParams = fetchOAuthParams()
          if (oauthParams) {
            // the popup URL has URL 'code' parameter
            externalPopup.close()
            setOAuthParams(oauthParams)
            // stop looking for code in popup window
            setExternalPopup(null);
            timer && clearInterval(timer);
          }
        } catch(exception:any) {
          if (exception.name == 'SecurityError') {
            /*
             * silence the security errors when attempting to access href http server
             * without Cross-Origin-Opener-Policy: same-origin-allow-popups
             */
            return
          } else {
            throw exception
          }
        }
      }, 500)
    }, [externalPopup])

    let authorizeButton = null
    if (authorizationUrl === LEGACY_GOOGLE_AUTHORIZATION_URL) {
      authorizeButton =
        <Button
          onClick={() => googleConnectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to Google" : "Disconnect"}
        </Button>
    } else if (authorizationUrl === GOOGLE_AUTHORIZATION_URL) {
      authorizeButton = (
        <Button
          onClick={() => connectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to Google" : "Disconnect"}
        </Button>
      )
    } else if (authorizationUrl === MICROSOFT_AUTHORIZATION_URL) {
      authorizeButton = (
        <Button
          onClick={() => connectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to Microsoft" : "Disconnect"}
        </Button>
      )
    } else if (authorizationUrl === FACEBOOK_AUTHORIZATION_URL) {
      authorizeButton = (
        <Button
          onClick={() => connectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to Facebook" : "Disconnect"}
        </Button>
      )
    } else if (authorizationUrl === LINKEDIN_AUTHORIZATION_URL) {
      authorizeButton = (
        <Button
          onClick={() => connectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to LinkedIn" : "Disconnect"}
        </Button>
      )
    } else if (authorizationUrl === QUICKBOOKS_AUTHORIZATION_URL) {
      authorizeButton = (
        <Button
          onClick={() => connectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to Quickbooks" : "Disconnect"}
        </Button>
      )
    } else if (authorizationUrl === XERO_AUTHORIZATION_URL) {
      authorizeButton = (
        <Button
          onClick={() => connectDisconnect()}>
          {!authorizationState.isAuthorized ? "Connect to Xero" : "Disconnect"}
        </Button>
      )
    } else {
      authorizeButton = (<div>Authorization url not supported: {authorizationUrl}</div>)
    }

    const content = (
      <>
        { authorizationState.isAuthorizationRequired ? authorizeButton : null}
        { errors && errors.length
            && errors.find((error) => error.field == (prefix ? `properties.${prefix}.oauth_credentials.access_token` : 'properties.oauth_credentials.access_token')
                                        || error.field == (prefix ? `properties.${prefix}.oauth_credentials.refresh_token` : 'properties.oauth_credentials.refresh_token'))
            ? (
            <p className="validation_msg">
                Data source must be authorized
            </p>
        ) : null}
      </>
    )

    useEffect(() => {
        if (oauthParams) {
          const authoriztionCode = oauthParams?.code
          // we have a code, but have not exchanged it for a access_token / refresh_token yet
          exchangeCode(authorizationUrl, authoriztionCode)
          if (authorizationUrl === QUICKBOOKS_AUTHORIZATION_URL) {
            // handle property specific to quick books
            properties[prefix ? `${prefix}.realm_id` : 'realm_id'] = oauthParams?.realmId
            handleSettingsUpdate(properties)
          }
        }
    }, [oauthParams]);

    useEffect(() => {
        if (accessToken) {
          if (authorizationUrl === 'https://auth.zappforms.com/connect/authorize') {
            // we have a Solarvista access_token, but have not exchanged this for clientId and code yet
            setAccessToken(null)
            properties[prefix ? `${prefix}.clientId` : 'clientId'] = 'todo'
            properties[prefix ? `${prefix}.code` : 'code'] = 'todo'
            handleSettingsUpdate(properties)
          }
        }
    }, [accessToken]);

    useEffect(() => {
      // the scope to be requested for this grant request
      const hasScope = !!properties[prefix ? `${prefix}.oauth_credentials.scope` : 'oauth_credentials.scope']
      // the presence of an access token used to indicate authorization
      const hasAccessToken = !!properties[prefix ? `${prefix}.oauth_credentials.access_token` : 'oauth_credentials.access_token']

      const isRequired = !!authorizationUrl && hasScope
      setAuthorizationState({isAuthorizationRequired: isRequired, isAuthorized: hasAccessToken})
      consentRequiredCallback(isRequired && !hasAccessToken)
    }, [properties]);

    return content;
};

export default DataPluginAuthorization;
